// src/components/PhotoUploadForm.jsx
import React, { useState } from 'react';
import { Camera, Upload, X, Film } from 'lucide-react';

const MediaUploadForm = ({ onSubmit }) => {
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        category: 'Events',
        files: []
    });
    const [previews, setPreviews] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
        const validVideoTypes = ['video/mp4', 'video/webm', 'video/quicktime'];
        const maxSize = 50 * 1024 * 1024; // 50MB

        // Validate files
        const invalidFiles = files.filter(file => {
            if (!validImageTypes.includes(file.type) && !validVideoTypes.includes(file.type)) {
                setError(`${file.name} is not a valid image or video type`);
                return true;
            }
            if (file.size > maxSize) {
                setError(`${file.name} is larger than 50MB`);
                return true;
            }
            return false;
        });

        if (invalidFiles.length > 0) return;

        // Create previews
        const newPreviews = files.map(file => ({
            file,
            preview: URL.createObjectURL(file),
            type: file.type.startsWith('video/') ? 'video' : 'photo'
        }));

        setFormData(prev => ({
            ...prev,
            files: [...prev.files, ...files]
        }));
        setPreviews(prev => [...prev, ...newPreviews]);
        setError('');
    };

    const removeFile = (index) => {
        setFormData(prev => ({
            ...prev,
            files: prev.files.filter((_, i) => i !== index)
        }));

        URL.revokeObjectURL(previews[index].preview);
        setPreviews(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError('');

        try {
            if (formData.files.length === 0) {
                throw new Error('Please select at least one file');
            }

            // Submit each file
            const submissions = formData.files.map(file =>
                onSubmit({
                    title: formData.title,
                    description: formData.description,
                    category: formData.category,
                    file: file,
                    fileType: file.type.startsWith('video/') ? 'video' : 'photo'
                })
            );

            await Promise.all(submissions);

            // Reset form
            setFormData({
                title: '',
                description: '',
                category: 'Events',
                files: []
            });

            previews.forEach(preview => {
                URL.revokeObjectURL(preview.preview);
            });
            setPreviews([]);

        } catch (error) {
            setError(error.message || 'Failed to upload files');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8 mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">Upload Media</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
                {/* File Upload */}
                <div className="space-y-4">
                    <input
                        type="file"
                        id="file-upload"
                        accept="image/*, video/*"
                        onChange={handleFileChange}
                        className="hidden"
                        multiple
                    />

                    {/* Preview Grid */}
                    {previews.length > 0 && (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mb-6">
                            {previews.map((preview, index) => (
                                <div
                                    key={index}
                                    className="relative group aspect-square rounded-lg overflow-hidden bg-gray-100"
                                >
                                    {preview.type === 'video' ? (
                                        <div className="relative w-full h-full">
                                            <video
                                                src={preview.preview}
                                                className="w-full h-full object-cover"
                                                controls
                                            />
                                            <div className="absolute top-2 left-2">
                                                <Film className="w-5 h-5 text-white drop-shadow-lg" />
                                            </div>
                                        </div>
                                    ) : (
                                        <img
                                            src={preview.preview}
                                            alt={`Preview ${index + 1}`}
                                            className="w-full h-full object-cover"
                                        />
                                    )}
                                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center
                                                  opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button
                                            type="button"
                                            onClick={() => removeFile(index)}
                                            className="p-2 bg-white/10 hover:bg-white/20 rounded-full
                                                     transition-colors transform hover:scale-110"
                                        >
                                            <X className="w-6 h-6 text-white" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Upload Button/Area */}
                    <label
                        htmlFor="file-upload"
                        className="block w-full h-52 border-2 border-dashed rounded-xl cursor-pointer
                                 border-purple-200 bg-purple-50/50 hover:bg-purple-50 hover:border-purple-300
                                 transition-all duration-200"
                    >
                        <div className="flex flex-col items-center justify-center h-full">
                            <div className="flex gap-3 mb-3">
                                <Camera className="w-8 h-8 text-purple-500" />
                                <Film className="w-8 h-8 text-purple-500" />
                            </div>
                            <div className="text-center">
                                <p className="text-base font-medium text-purple-600 mb-1">
                                    Click to upload media
                                </p>
                                <p className="text-sm text-purple-500">
                                    Images or Videos
                                </p>
                                <p className="text-xs text-purple-400 mt-2">
                                    Maximum file size: 50MB
                                </p>
                            </div>
                        </div>
                    </label>
                </div>

                {/* Form Fields */}
                <div className="space-y-4">
                    <div>
                        <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                            Title
                        </label>
                        <input
                            id="title"
                            type="text"
                            placeholder="Give your media a title"
                            value={formData.title}
                            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg
                                     focus:ring-2 focus:ring-purple-100 focus:border-purple-400
                                     transition duration-200"
                            required
                        />
                    </div>

                    <div>
                        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                            Description
                        </label>
                        <textarea
                            id="description"
                            placeholder="Add a description"
                            value={formData.description}
                            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                            rows={3}
                            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg
                                     focus:ring-2 focus:ring-purple-100 focus:border-purple-400
                                     transition duration-200"
                            required
                        />
                    </div>

                    <div>
                        <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                            Category
                        </label>
                        <select
                            id="category"
                            value={formData.category}
                            onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg
                                     focus:ring-2 focus:ring-purple-100 focus:border-purple-400
                                     transition duration-200"
                        >
                            <option value="Events">Events</option>
                            <option value="Education">Education</option>
                            <option value="Health">Health</option>
                            <option value="Community">Community</option>
                            <option value="Projects">Projects</option>
                        </select>
                    </div>
                </div>

                {error && (
                    <div className="p-4 rounded-lg bg-red-50 border border-red-100">
                        <p className="text-sm text-red-600">{error}</p>
                    </div>
                )}

                <button
                    type="submit"
                    disabled={isSubmitting || formData.files.length === 0}
                    className="w-full inline-flex items-center justify-center gap-2 px-6 py-3
                             bg-purple-600 hover:bg-purple-700 text-white rounded-lg
                             transition-colors duration-200 font-medium
                             disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <Upload className="h-5 w-5" />
                    {isSubmitting ? 'Uploading...' : `Upload ${formData.files.length} File${formData.files.length !== 1 ? 's' : ''}`}
                </button>
            </form>
        </div>
    );
};

export default MediaUploadForm;